import { useQuery } from '@tanstack/react-query';
import type { IssueSourceKind, IssueSummary } from '@chatsdet/types/issues';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

type FetchParams = {
  sourceKind: IssueSourceKind;
  sourceId: string;
};

type UseIssuesBySourceOptions = {
  enabled?: boolean;
};

const fetchIssuesBySource = async (params: FetchParams): Promise<IssueSummary[]> => {
  const search = new URLSearchParams({
    sourceKind: params.sourceKind,
    sourceId: params.sourceId,
  });
  const response = await fetch(`${API_URL}/issues/by-source?${search.toString()}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw await toApiError(response, 'Failed to load linked issues');
  }

  const payload = (await response.json()) as { ok: boolean; data?: IssueSummary[] };
  if (!payload.ok || !Array.isArray(payload.data)) {
    throw new Error('Failed to load linked issues');
  }

  return payload.data;
};

export const useIssuesBySource = (
  params: FetchParams,
  options?: UseIssuesBySourceOptions
) => {
  const enabled = options?.enabled ?? true;
  const trimmedSourceId = params.sourceId.trim();

  return useQuery({
    queryKey: ['issues', 'by-source', params.sourceKind, trimmedSourceId],
    queryFn: () => fetchIssuesBySource({ ...params, sourceId: trimmedSourceId }),
    enabled: enabled && Boolean(params.sourceKind) && Boolean(trimmedSourceId),
    staleTime: 30_000,
  });
};
